/**
 * Excel exporter — graph + object types → XLSX workbook → file download.
 *
 * `xlsx` is loaded via dynamic import on first use so the library is
 * absent from the initial bundle (Plan §4 NFR: bundle ≤ +250KB).
 *
 * The exporter is graph-aware but JointJS-agnostic: it accepts any object
 * matching the minimal `GraphLike` shape so unit tests can inject a plain
 * mock without instantiating JointJS.
 */

import type { ExcelColumn, ExcelSheetSpec } from '../model/types'
import type { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import { buildAllSheetSpecs } from './columnSchema'
import { FILENAME_EXTENSION, FILENAME_PREFIX, FILENAME_TEMPLATE_PREFIX } from './constants'

// ─────────────────────────────────────────────────────────────────────────────
// JointJS-agnostic graph contract
// ─────────────────────────────────────────────────────────────────────────────

/** Minimal element shape the exporter needs from a graph. */
export interface ElementLike {
  id: string | number
  get(key: 'data'):
    | {
        typeId?: string
        /** Legacy name-based discriminator used by older / CSV-imported elements. */
        type?: string
        /** Yet another legacy field — see elementConverter.ts:60. */
        objectType?: string
        /** Some older saves stored the display name here instead of properties.name. */
        text?: string
        properties?: Record<string, unknown>
      }
    | undefined
  /** JointJS `prop('objectTypeId')` fallback used by older creation paths. */
  prop?(path: 'objectTypeId'): string | undefined
}

/** Minimal graph shape: must yield an iterable of elements. */
export interface GraphLike {
  getElements(): ElementLike[]
}

// ─────────────────────────────────────────────────────────────────────────────
// xlsx dynamic loader (cached at module level)
// ─────────────────────────────────────────────────────────────────────────────

type XLSXModule = typeof import('xlsx')

let xlsxPromise: Promise<XLSXModule> | null = null

async function loadXlsx(): Promise<XLSXModule> {
  if (!xlsxPromise) {
    xlsxPromise = import('xlsx')
  }
  return xlsxPromise
}

// ─────────────────────────────────────────────────────────────────────────────
// Filename helpers
// ─────────────────────────────────────────────────────────────────────────────

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

/** Format a date as `YYYYMMDD-HHmm` in local time. */
export function formatTimestamp(date: Date = new Date()): string {
  const yyyy = date.getFullYear()
  const mm = pad2(date.getMonth() + 1)
  const dd = pad2(date.getDate())
  const HH = pad2(date.getHours())
  const MM = pad2(date.getMinutes())
  return `${yyyy}${mm}${dd}-${HH}${MM}`
}

/** Sanitize a map name for use in a filename (strip path-unfriendly chars). */
function sanitizeMapName(name: string): string {
  return (
    name
      .trim()
      .replace(/[/\\?%*:|"<>]/g, '_')
      .replace(/\s+/g, '-')
      .slice(0, 80) || 'untitled'
  )
}

export function buildExportFilename(mapName: string, date: Date = new Date()): string {
  return `${FILENAME_PREFIX}-${sanitizeMapName(mapName)}-${formatTimestamp(date)}.${FILENAME_EXTENSION}`
}

export function buildTemplateFilename(date: Date = new Date()): string {
  return `${FILENAME_TEMPLATE_PREFIX}-${formatTimestamp(date)}.${FILENAME_EXTENSION}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Row building
// ─────────────────────────────────────────────────────────────────────────────

/** Read a single column value from a cell. Empty/undefined → empty string for Excel. */
function readColumnValue(
  element: ElementLike,
  column: ExcelColumn
): unknown {
  const data = element.get('data') ?? {}
  const props = (data.properties ?? {}) as Record<string, unknown>

  switch (column.source) {
    case 'identity':
      return String(element.id)
    case 'name': {
      // Mirror elementConverter.ts:61 — `properties.name || data.text` — so cells
      // imported via CSV / older saves that stored the display name on `data.text`
      // still produce a non-empty name column.
      const fromProps = props.name
      if (fromProps !== undefined && fromProps !== null && fromProps !== '') {
        return fromProps
      }
      const fromText = data.text
      if (fromText !== undefined && fromText !== null && fromText !== '') {
        return fromText
      }
      return ''
    }
    case 'property': {
      const v = props[column.key]
      if (v === undefined || v === null) return ''
      // Arrays are exported as comma-joined strings (relationship arrays are
      // already excluded; this only matters for misc array properties).
      if (Array.isArray(v)) return v.join(', ')
      return v
    }
  }
}

/** Convert an element row into an array-of-arrays line aligned with the sheet's columns. */
function buildElementRow(element: ElementLike, spec: ExcelSheetSpec): unknown[] {
  return spec.columns.map(col => readColumnValue(element, col))
}

/** Build the AOA (array-of-arrays) for one sheet: header + element rows. */
export function buildSheetRows(
  spec: ExcelSheetSpec,
  elements: ElementLike[]
): unknown[][] {
  const header = spec.columns.map(c => c.header)
  const dataRows = elements.map(el => buildElementRow(el, spec))
  return [header, ...dataRows]
}

/**
 * Build a lookup that maps any of `{ObjectType.id, ObjectType.name}` (case-insensitive
 * for names) to the canonical `ObjectType.id`. This mirrors the resolution chain used
 * by PropertyEditor/EditorSidebar so the exporter can include legacy/CSV-imported
 * elements that only carry `data.type` (a name) instead of `data.typeId` (a UUID).
 */
function buildTypeIdAliasMap(objectTypes: ObjectType[]): Map<string, string> {
  const aliases = new Map<string, string>()
  for (const t of objectTypes) {
    aliases.set(t.id, t.id)
    if (t.name) aliases.set(t.name.toLowerCase(), t.id)
  }
  return aliases
}

/**
 * Optional caller-supplied resolver. When provided, it runs FIRST so an editor
 * with access to mapping stores (entity-handle mappings, layer-type mappings)
 * can recover a typeId that isn't on the element itself.
 *
 * Mirrors PropertyEditor.tsx steps 3+4 (`data.entityHandle` → mappings,
 * `data.layer + floorId` → layerTypeMappings) which the exporter cannot do
 * by itself because those stores aren't bundled with the graph.
 */
export type TypeIdResolver = (element: ElementLike) => string | undefined

/** Resolve an element's effective ObjectType id using the same chain as the editor. */
function resolveElementTypeId(
  element: ElementLike,
  aliases: Map<string, string>,
  external?: TypeIdResolver
): string | undefined {
  const data = element.get('data')
  const direct = data?.typeId
  if (direct && aliases.has(direct)) return aliases.get(direct)
  const propId = element.prop?.('objectTypeId')
  if (propId && aliases.has(propId)) return aliases.get(propId)
  // Legacy name-keyed fields used by older saves and CSV-imported elements.
  // Mirror elementConverter.ts:60 — `data.typeId || data.objectType || data.type`.
  const legacyObjectType = data?.objectType
  if (legacyObjectType && aliases.has(legacyObjectType.toLowerCase())) {
    return aliases.get(legacyObjectType.toLowerCase())
  }
  const legacyType = data?.type
  if (legacyType && aliases.has(legacyType.toLowerCase())) {
    return aliases.get(legacyType.toLowerCase())
  }
  // External resolver (mapping stores) — the editor passes this when it has
  // access to entity-handle / layer-type mappings.
  if (external) {
    const ext = external(element)
    if (ext && aliases.has(ext)) return aliases.get(ext)
    if (ext && aliases.has(ext.toLowerCase())) return aliases.get(ext.toLowerCase())
  }
  // Last resort: a UUID-shaped data.typeId that we don't have an ObjectType for —
  // still bucket it so downstream callers can decide whether to drop or surface it.
  return direct || undefined
}

/**
 * Group all graph elements by canonical ObjectType id.
 * Elements without any resolvable typeId are skipped.
 *
 * `objectTypes` is optional only to keep older callers compiling; passing it is
 * required to recover legacy/CSV-imported elements whose `data.typeId` is a name
 * rather than a UUID.
 */
export function groupElementsByTypeId(
  elements: ElementLike[],
  objectTypes: ObjectType[] = [],
  resolver?: TypeIdResolver
): Map<string, ElementLike[]> {
  const aliases = buildTypeIdAliasMap(objectTypes)
  const byType = new Map<string, ElementLike[]>()
  for (const el of elements) {
    const typeId = resolveElementTypeId(el, aliases, resolver)
    if (!typeId) continue
    const list = byType.get(typeId)
    if (list) {
      list.push(el)
    } else {
      byType.set(typeId, [el])
    }
  }
  return byType
}

// ─────────────────────────────────────────────────────────────────────────────
// Workbook building
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build an xlsx WorkBook from a graph + object types.
 *
 * Behavior:
 *   - One sheet per ObjectType (sheet name = ObjectType.name).
 *   - Empty types still get a header-only sheet, so the file is a valid template.
 *   - Elements whose typeId is not in `objectTypes` are silently skipped
 *     (e.g. legacy or partially-initialized cells).
 */
export async function buildWorkbook(
  graph: GraphLike,
  objectTypes: ObjectType[],
  resolver?: TypeIdResolver
): Promise<import('xlsx').WorkBook> {
  const XLSX = await loadXlsx()
  const wb = XLSX.utils.book_new()
  const specs = buildAllSheetSpecs(objectTypes)
  const allElements = graph.getElements()
  const grouped = groupElementsByTypeId(allElements, objectTypes, resolver)

  let matched = 0
  for (const spec of specs) {
    const elements = grouped.get(spec.typeId) ?? []
    matched += elements.length
    const aoa = buildSheetRows(spec, elements)
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    XLSX.utils.book_append_sheet(wb, ws, spec.sheetName)
  }

  // Diagnostic — fires once per export. Helps users verify that elements were
  // actually resolved instead of silently dropped (typeId mismatch, empty graph,
  // wrong floor, etc.).
  /* eslint-disable no-console */
  console.info(
    `[excel-export] elements=${allElements.length} matched=${matched} ` +
    `unmatched=${allElements.length - matched} sheets=${specs.length}`
  )
  if (allElements.length > 0 && matched < allElements.length) {
    // Show up to 5 unmatched elements' raw discriminators so the user can
    // tell us what's actually on those cells.
    const unmatchedSamples = allElements
      .filter(el => {
        const data = el.get('data')
        const direct = data?.typeId
        if (direct && specs.some(s => s.typeId === direct)) return false
        return true
      })
      .slice(0, 5)
      .map(el => {
        const d = el.get('data') ?? {}
        return {
          id: el.id,
          typeId: d.typeId,
          type: d.type,
          objectType: d.objectType,
          propObjectTypeId: el.prop?.('objectTypeId'),
        }
      })
    console.warn(
      '[excel-export] unmatched elements (first 5):',
      unmatchedSamples,
      'available ObjectType ids:',
      objectTypes.map(t => ({ id: t.id, name: t.name }))
    )
  }
  /* eslint-enable no-console */

  return wb
}

/** Build a header-only template workbook (no rows). */
export async function buildTemplateWorkbook(
  objectTypes: ObjectType[]
): Promise<import('xlsx').WorkBook> {
  const XLSX = await loadXlsx()
  const wb = XLSX.utils.book_new()
  const specs = buildAllSheetSpecs(objectTypes)

  for (const spec of specs) {
    const aoa = buildSheetRows(spec, [])
    const ws = XLSX.utils.aoa_to_sheet(aoa)
    XLSX.utils.book_append_sheet(wb, ws, spec.sheetName)
  }

  return wb
}

// ─────────────────────────────────────────────────────────────────────────────
// Public entry points
// ─────────────────────────────────────────────────────────────────────────────

/** Build the workbook and trigger a browser download. */
export async function exportMapToExcel(
  graph: GraphLike,
  objectTypes: ObjectType[],
  mapName: string,
  now: Date = new Date(),
  resolver?: TypeIdResolver
): Promise<void> {
  const XLSX = await loadXlsx()
  const wb = await buildWorkbook(graph, objectTypes, resolver)
  const filename = buildExportFilename(mapName, now)
  XLSX.writeFile(wb, filename)
}

/** Build a header-only template workbook and trigger a browser download. */
export async function exportEmptyTemplate(
  objectTypes: ObjectType[],
  now: Date = new Date()
): Promise<void> {
  const XLSX = await loadXlsx()
  const wb = await buildTemplateWorkbook(objectTypes)
  const filename = buildTemplateFilename(now)
  XLSX.writeFile(wb, filename)
}
